import { User, UserRole } from '@nextforce/api'
import { Accessory } from '@rneui/base/dist/Avatar/Avatar.Accessory'
import { Avatar, Text, makeStyles } from '@rneui/themed'
import getEnvironment from 'app/environment'
import { t } from 'app/i18l'
import { usePolicies } from 'app/policies'
import { font } from 'app/theme'
import * as Application from 'expo-application'
import * as Updates from 'expo-updates'
import React from 'react'
import { ActivityIndicator, Platform, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { confirmationAlert } from 'shared/alerts'
import { GoldStar } from 'shared/components/gold_star'
import LeftArrowBanner from 'shared/components/left_arrow_banner'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { RoundedButton } from 'shared/components/rounded-button'
import { SupportButton } from 'shared/components/support_button'
import { pickImageFromLibrary } from 'shared/image_picker'
import { WebScreenParams } from 'web/types'
import { EnvName } from './dev_tools/types'

interface Props {
  user: User | undefined
  isPhotoUploading: boolean
  onLogout?: () => Promise<void>
  onSettings: () => void
  onEditStoreSettings: () => void
  onAvatarChange: (avatar: string, mimeType: string) => void
  onAvatarRemove: () => void
  onNameChange: () => void
  onLinkPress: (webParams: WebScreenParams) => void
  role?: UserRole
  onDevTools: () => void
  onShareApp: () => void
  localBaseURLEnvName: EnvName | undefined
  isUserWebForCrew: boolean
  goldStars?: number
}

export function AccountView({
  user,
  isPhotoUploading,
  onLogout,
  onSettings,
  onAvatarChange,
  onAvatarRemove,
  onLinkPress,
  onEditStoreSettings,
  onNameChange,
  onDevTools,
  onShareApp,
  role,
  goldStars,
  localBaseURLEnvName,
  isUserWebForCrew,
}: Props) {
  const styles = useStyles()
  const policies = usePolicies()

  if (!user) {
    return null
  }

  const envName = getEnvironment().name
  const version = Application?.nativeApplicationVersion
  const update = Updates.isEmbeddedLaunch ? null : Updates.updateId
  const channel = Updates.channel

  const uploadImage = async () => {
    const pickImageResult = await pickImageFromLibrary()
    if (pickImageResult.status === 'success') {
      onAvatarChange(
        pickImageResult.files[0].uri,
        pickImageResult.files[0].mimeType
      )
    }
  }

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <View style={styles.avatarRow}>
          {isPhotoUploading ? (
            <View style={styles.imageActivityContainer}>
              <ActivityIndicator />
            </View>
          ) : (
            <Avatar
              key={user.photoUrl}
              rounded
              size="large"
              title={!!user.photoUrl ? undefined : user.initials}
              source={user.photoUrl ? { uri: user.photoUrl } : undefined}
              titleStyle={styles.avatarTitle}
              containerStyle={styles.avatarContainer}
              onPress={uploadImage}
            >
              {Platform.OS === 'web' ? null : (
                <Avatar.Accessory onPress={uploadImage} size={20} />
              )}
              {user.hasProfilePhoto && (
                <Avatar.Accessory
                  type="font-awesome-5"
                  name="times"
                  solid
                  size={20}
                  style={{ left: 0 }}
                  onPress={async () => {
                    const isConfirmed = await confirmationAlert(
                      t('removePhotoTitle'),
                      t('removePhotoText')
                    )
                    if (isConfirmed) {
                      onAvatarRemove()
                    }
                  }}
                />
              )}
            </Avatar>
          )}
          {!user.hasProfilePhoto && Platform.OS !== 'web' ? (
            <PressableOpacity style={{ flex: 1 }} onPress={uploadImage}>
              <LeftArrowBanner bannerText={t('uploadPhoto')} />
            </PressableOpacity>
          ) : null}
        </View>
        <View>
          <Text style={styles.profileUserName}>
            {user.firstName} {user.lastName}{' '}
            <Accessory
              style={styles.accessory}
              size={20}
              onPress={onNameChange}
            />
          </Text>
        </View>
        {goldStars ? (
          <View style={styles.goldStarsContainer}>
            <Text style={styles.attributesText}>{goldStars}</Text>
            <GoldStar />
            <Text style={styles.attributesText}>
              {t('star', { count: goldStars })}
            </Text>
          </View>
        ) : null}
        <Text style={styles.loginText}>{`${t('loggedInAs')} ${
          user.phoneNumber
        }`}</Text>
        <RoundedButton
          containerStyle={styles.buttonContainer}
          title={t('editMyProfile')}
          onPress={onEditStoreSettings}
        />
        <RoundedButton
          containerStyle={styles.buttonContainer}
          title={t('settings')}
          onPress={onSettings}
        />
        {onLogout && (
          <RoundedButton
            containerStyle={styles.buttonContainer}
            title={t('signOut')}
            onPress={onLogout}
            emphasized
          />
        )}

        <View style={styles.privacyTermsContainer}>
          <Text
            style={styles.privacyPolicy}
            onPress={() => onLinkPress(policies.privacy)}
          >
            {policies.privacy.title}
          </Text>
          <Text
            style={styles.terms}
            onPress={() => onLinkPress(policies.termsOfUse)}
          >
            {policies.termsOfUse.title}
          </Text>
        </View>
        <Text style={styles.version}>
          {`${version ? `${t('version')}: ${version}` : ''}
${envName !== 'production' ? `Environment: ${envName}` : ''}
${update ? `\n${t('update')}: ${update}` : ''}
${envName !== 'production' && channel ? `\nChannel: ${channel}` : ''}`}
        </Text>
        {user?.isTestUser || localBaseURLEnvName ? (
          <Text style={styles.developerLink} onPress={onDevTools}>
            {`Developer [${localBaseURLEnvName ?? envName}]`}
          </Text>
        ) : null}
        <RoundedButton
          containerStyle={styles.buttonContainer}
          title={t('shareNextWithCoworkers')}
          onPress={onShareApp}
          titleStyle={styles.shareAppButtonTitle}
          buttonStyle={styles.shareAppButton}
          danger
        />
        {user && role && !isUserWebForCrew && (
          <View style={styles.floatingView}>
            <SupportButton />
          </View>
        )}
      </View>
    </SafeAreaView>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    padding: 20,
    flex: 1,
  },
  avatarRow: { flexDirection: 'row', justifyContent: 'space-between' },
  avatarContainer: {
    backgroundColor: '#FFE2B3',
  },
  imageActivityContainer: {
    width: 75,
    height: 75,
    borderRadius: 100,
    justifyContent: 'center',
    backgroundColor: '#FFE2B3',
  },
  avatarTitle: {
    color: '#353B4A',
    fontSize: 30,
    fontFamily: font.bold,
  },
  accessory: {
    position: 'relative',
    marginLeft: 5,
    marginBottom: 5,
  },
  profileUserName: {
    fontSize: 28,
    fontFamily: font.bold,
    marginTop: 10,
    flexShrink: 1,
  },
  loginText: {
    fontSize: 17,
    fontFamily: font.bold,
    color: 'gray',
    marginTop: 5,
  },
  buttonContainer: {
    marginTop: 10,
  },
  privacyTermsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  privacyPolicy: {
    textAlign: 'left',
    color: 'gray',
    textDecorationLine: 'underline',
  },
  terms: {
    textAlign: 'right',
    color: 'gray',
    textDecorationLine: 'underline',
  },
  version: {
    color: 'gray',
    marginTop: 20,
    textAlign: 'center',
  },
  floatingView: {
    position: 'absolute',
    bottom: 20,
    right: 20,
  },
  developerLink: {
    textAlign: 'center',
    color: theme.colors?.blue,
    marginTop: 10,
  },
  shareAppButtonTitle: {
    color: theme.colors?.primary,
    fontWeight: 'normal',
  },
  shareAppButton: {
    backgroundColor: '#FBE2B9',
  },
  goldStarsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  attributesText: {
    fontSize: 16,
    justifyContent: 'center',
    padding: 10,
    alignItems: 'stretch',
  },
}))
